import { createNode } from '@glueapp/graph-types';
import {
    extractAllPaths,
    computeOutputTypeForPath,
    disambiguatePortNames,
    extractValueAtPath,
    type UnpackSelections,
} from './unpackHelpers';

export const Unpack = createNode({
    id: 'Unpack',
    name: 'Unpack',
    description:
        'Unpack and extract nested properties from objects and lists. Also known as: Get Properties, Explode, Explore, Destructure, Extract, Pick Fields.',
    aiDescription: [
        'The Unpack node extracts nested properties from an object or list and exposes them as individual output ports.',
        '',
        'STATE CONFIGURATION:',
        'The node state has two keys:',
        '- "selections": an object where each key is a dot-path to a property (e.g. "user.address.city" or "items.name") and the value configures how that property is extracted.',
        '- "expanded": boolean controlling the UI view mode (true = full tree, false = compact).',
        '',
        'SELECTION VALUES:',
        'For properties with NO list ancestor: just include the path with an empty object: { "user.name": {} }',
        'For properties WITH a list ancestor, set "listMode" to one of:',
        '  - "flat-list": flattens all nested list levels into a single list (default, most common)',
        '  - "nested-list": preserves the nesting structure (e.g. string[][] for two list levels)',
        '  - "pick-item": extracts a single value by index at each list level. Requires "pickedIndexes" mapping each list ancestor\'s dot-path to a 0-based index.',
        '',
        'EXAMPLES:',
        'Input type: { orders: { id: number, items: { name: string, price: number }[] }[] }',
        '',
        'To get a flat list of all item names:',
        '  selections: { "orders.items.name": { "listMode": "flat-list" } }',
        '  → output port "name" with type string[]',
        '',
        'To get order IDs as a list:',
        '  selections: { "orders.id": { "listMode": "flat-list" } }',
        '  → output port "id" with type number[]',
        '',
        'To get the name of item 0 from order 0:',
        '  selections: { "orders.items.name": { "listMode": "pick-item", "pickedIndexes": { "orders": 0, "orders.items": 0 } } }',
        '  → output port "name" with type string',
        '',
        'To get all properties with defaults: include all leaf paths with { "listMode": "flat-list" } for any that have list ancestors, or {} for pure object paths.',
        '',
        'OUTPUT PORT NAMING: ports are named after the leaf property. If two properties share a leaf name, they are disambiguated with their parent (e.g. "address.city" vs "billing.city").',
    ].join('\n'),
    category: 'Object',
    color: '#a17ee2',
    integration: undefined,
    inPorts: {
        Input: {
            kind: 'generic',
            identifier: 'T',
            extends: {
                kind: 'union',
                subTypes: [
                    {
                        kind: 'object',
                        restriction: 'open',
                        properties: {},
                    },
                    {
                        kind: 'array',
                        subType: {
                            kind: 'object',
                            restriction: 'open',
                            properties: {},
                        },
                    },
                ],
            },
        },
    },
    outPorts: {},
    computeShape: input => {
        const inputPort = input.preResolvedInputPorts.find(port => port.name === 'Input');
        if (!inputPort?.type) {
            return { inPorts: input.nodeInPorts, outPorts: [] };
        }

        // Unwrap generic to get the resolved type
        let resolvedType = inputPort.type;
        if (resolvedType.kind === 'generic' && resolvedType.resolvedType) {
            resolvedType = resolvedType.resolvedType;
        }

        if (resolvedType.kind !== 'object' && resolvedType.kind !== 'array') {
            return { inPorts: input.nodeInPorts, outPorts: [] };
        }

        const selections: UnpackSelections = (input.state?.selections as UnpackSelections) || {};
        const allPaths = extractAllPaths(resolvedType);

        // Build path info lookup (containers + leaves)
        const pathInfoMap = new Map(allPaths.map(pathInfo => [pathInfo.dotPath, pathInfo]));

        // Filter to only selected paths
        const selectedPaths = Object.keys(selections).filter(dotPath => pathInfoMap.has(dotPath));

        if (selectedPaths.length === 0) {
            return { inPorts: [inputPort], outPorts: [] };
        }

        // Compute disambiguated port names
        const portNames = disambiguatePortNames(selectedPaths);

        // Generate output ports
        const outPorts = selectedPaths.map(dotPath => {
            const pathInfo = pathInfoMap.get(dotPath)!;
            const selection = selections[dotPath];
            const outputType = computeOutputTypeForPath(pathInfo.leafType, pathInfo.listAncestorPaths, selection);
            return {
                name: portNames[dotPath],
                type: outputType,
            };
        });

        return { inPorts: [inputPort], outPorts };
    },
    execute: function (input) {
        const inputValue = input.inPorts.Input;
        const selections: UnpackSelections = (input.state?.selections as UnpackSelections) || {};

        if (inputValue == null || Object.keys(selections).length === 0) {
            return { output: {} };
        }

        // Build a comprehensive path→info map from the runtime value. Unlike a
        // type walker (which execute doesn't have access to here), this operates
        // on the actual value, so we visit *every* element of *every* array and
        // union results. That protects against two failure modes:
        //   (a) the first element of an array is null or a primitive (hiding
        //       the shape of later elements), and
        //   (b) array elements are heterogeneous (different elements expose
        //       different keys).
        const pathInfoMap = buildRuntimePathInfo(inputValue);

        // Mirror computeShape's staleness filter. computeShape drops any
        // selection whose dotPath doesn't exist in the type, so only the
        // surviving paths feed into port-name disambiguation. If execute
        // disambiguates over a *different* set of paths — e.g. state contains
        // a leftover path from a previous input type — the generated port
        // names can drift from those computeShape declared, and downstream
        // consumers see the wrong port. So filter selection keys here too,
        // using the runtime pathInfoMap as the reachability oracle (the
        // closest thing to the type we have at execution time).
        const selectedPaths = Object.keys(selections).filter(dotPath => isPathReachable(pathInfoMap, dotPath));

        if (selectedPaths.length === 0) {
            return { output: {} };
        }

        const portNames = disambiguatePortNames(selectedPaths);

        const output: { [key: string]: any } = {};

        for (const dotPath of selectedPaths) {
            const selection = selections[dotPath];
            const listAncestorPaths = resolveListAncestors(pathInfoMap, dotPath);
            const portName = portNames[dotPath];
            output[portName] = extractValueAtPath(inputValue, dotPath, selection, listAncestorPaths);
        }

        return { output };
    },
});

interface RuntimePathInfo {
    listAncestorPaths: string[];
}

/**
 * Walk a runtime value and record, for every reachable dot-path, which
 * prefixes along the way were arrays. Unlike the previous implementation this
 * visits every array element (not just [0]) and also records entries for
 * null-valued containers, so heterogeneous shapes and missing/null first
 * elements don't cause paths to disappear from the map.
 */
function buildRuntimePathInfo(value: any): Map<string, RuntimePathInfo> {
    const map = new Map<string, RuntimePathInfo>();
    walkRuntimeValue(value, '', [], map, false);
    return map;
}

function walkRuntimeValue(
    value: any,
    currentPath: string,
    listAncestorPaths: string[],
    map: Map<string, RuntimePathInfo>,
    skipObjectContainerEmission: boolean,
): void {
    // Null/undefined still records the path. Callers use this map as a
    // reachability check, and a type-valid path like `user.name` must remain
    // "reachable" when the runtime has `user: null` so we still emit its port
    // (with a null value) — matching what computeShape declared.
    if (value == null) {
        if (currentPath !== '' && !map.has(currentPath)) {
            map.set(currentPath, { listAncestorPaths: [...listAncestorPaths] });
        }
        return;
    }

    if (Array.isArray(value)) {
        const newListAncestors = [...listAncestorPaths, currentPath];
        if (currentPath !== '' && !map.has(currentPath)) {
            map.set(currentPath, { listAncestorPaths: newListAncestors });
        }
        // Visit every element. First-entry-only traversal used to lose paths
        // whenever value[0] was null / primitive / a different variant.
        for (const item of value) {
            if (item == null) continue;
            if (typeof item === 'object') {
                // Recurse into objects and nested arrays alike. Pass
                // skipObjectContainerEmission=true so the inner object doesn't
                // re-emit the array container's dot-path.
                walkRuntimeValue(item, currentPath, newListAncestors, map, true);
            }
            // Primitive elements are covered by the array container entry above.
        }
        return;
    }

    if (typeof value === 'object') {
        if (currentPath !== '' && !skipObjectContainerEmission && !map.has(currentPath)) {
            map.set(currentPath, { listAncestorPaths: [...listAncestorPaths] });
        }
        for (const [key, childValue] of Object.entries(value)) {
            const childPath = currentPath ? `${currentPath}.${key}` : key;
            walkRuntimeValue(childValue, childPath, listAncestorPaths, map, false);
        }
        return;
    }

    // Primitive leaf
    if (!map.has(currentPath)) {
        map.set(currentPath, { listAncestorPaths: [...listAncestorPaths] });
    }
}

/**
 * A selection dot-path is considered reachable if the runtime walker emitted
 * it directly OR any ancestor prefix was emitted. The ancestor-prefix case
 * covers type-valid paths whose leaf value happens to be absent at runtime
 * (e.g. an optional field missing from this payload) — we still want to emit
 * a port for those, populated with null. Paths whose ancestors don't exist in
 * the value at all are treated as stale (e.g. leftover state from a previous
 * input type) and dropped, so they can't perturb port-name disambiguation.
 */
function isPathReachable(map: Map<string, RuntimePathInfo>, dotPath: string): boolean {
    if (map.has(dotPath)) return true;
    const segments = dotPath.split('.');
    for (let end = segments.length - 1; end >= 1; end--) {
        const ancestor = segments.slice(0, end).join('.');
        if (map.has(ancestor)) return true;
    }
    return false;
}

/**
 * Resolve the list-ancestor paths for a selection. Prefers the walker's exact
 * entry; falls back to the closest ancestor's ancestors (segments beyond a
 * missing ancestor can't be arrays — their values aren't in the data — so the
 * ancestor's ancestor set is the right answer).
 */
function resolveListAncestors(map: Map<string, RuntimePathInfo>, dotPath: string): string[] {
    const exact = map.get(dotPath);
    if (exact) return exact.listAncestorPaths;
    const segments = dotPath.split('.');
    for (let end = segments.length - 1; end >= 1; end--) {
        const ancestor = segments.slice(0, end).join('.');
        const info = map.get(ancestor);
        if (info) return info.listAncestorPaths;
    }
    return [];
}
